import { supabase } from "@/integrations/supabase/client";

export type ActivityEventType = 
  // Auth events
  | 'signup'
  | 'first_login'
  | 'login'
  // Magic onboarding
  | 'magic_page_view'
  | 'magic_search_started'
  | 'magic_search_success'
  | 'magic_search_failed'
  | 'magic_email_captured'
  | 'magic_continue_to_auth'
  // OCR flow
  | 'ocr_page_view'
  | 'ocr_photo_selected'
  | 'ocr_analysis_started'
  | 'ocr_analysis_success'
  | 'ocr_analysis_failed'
  | 'ocr_menu_created'
  // Onboarding steps
  | 'onboarding_start'
  | 'onboarding_step_email'
  | 'onboarding_step_restaurant'
  | 'onboarding_step_location'
  | 'onboarding_step_contact'
  | 'onboarding_step_confirmation'
  | 'onboarding_completed'
  // Tutorial
  | 'tutorial_started'
  | 'tutorial_step_completed'
  | 'tutorial_skipped'
  | 'tutorial_completed'
  // Restaurant & Menu
  | 'restaurant_created'
  | 'menu_created'
  | 'menu_item_added'
  | 'menu_published'
  | 'first_menu_view'
  // Features
  | 'qr_downloaded'
  | 'reservation_enabled'
  | 'happy_hour_enabled'
  | 'site_enabled'
  | 'subscription_started'
  // Dropoff indicators
  | 'page_exit'
  | 'session_timeout'
  // Hotel events
  | 'hotel_ocr_page_view'
  | 'hotel_ocr_photo_selected'
  | 'hotel_ocr_analysis_started'
  | 'hotel_ocr_analysis_success'
  | 'hotel_ocr_analysis_failed'
  | 'hotel_ocr_menu_created'
  | 'hotel_onboarding_start'
  | 'hotel_onboarding_step'
  | 'hotel_onboarding_completed'
  | 'hotel_signup_view'
  | 'hotel_signup_completed'
  | 'hotel_setup_step'
  | 'hotel_preview_view';

interface TrackingData {
  [key: string]: any;
}

// Track without requiring auth (for anonymous/pre-signup tracking)
export async function trackAnonymous(
  eventType: string,
  eventData: TrackingData = {}
): Promise<void> {
  try {
    // Use localStorage to generate consistent anonymous ID
    let anonId = localStorage.getItem('anon_tracking_id');
    if (!anonId) {
      anonId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('anon_tracking_id', anonId);
    }

    console.log(`[TRACK] ${eventType}`, { anonId, ...eventData });

    // Store in sessionStorage for later sync when user signs up
    const pendingEvents = JSON.parse(sessionStorage.getItem('pending_tracking_events') || '[]');
    pendingEvents.push({
      event_type: eventType,
      event_data: { ...eventData, anon_id: anonId },
      created_at: new Date().toISOString(),
    });
    sessionStorage.setItem('pending_tracking_events', JSON.stringify(pendingEvents));
  } catch (err) {
    console.error('Failed to track anonymous activity:', err);
  }
}

export async function trackActivity(
  eventType: ActivityEventType,
  eventData: TrackingData = {},
  restaurantId?: string
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      // Fall back to anonymous tracking
      trackAnonymous(eventType, eventData);
      return;
    }

    console.log(`[TRACK] ${eventType}`, { userId: user.id, restaurantId, ...eventData });

    const { error } = await supabase
      .from('user_activity_tracking')
      .insert({
        user_id: user.id,
        restaurant_id: restaurantId || null,
        event_type: eventType,
        event_data: eventData,
      });

    if (error) {
      console.error('Error tracking activity:', error);
    }
  } catch (err) {
    console.error('Failed to track activity:', err);
  }
}

// Sync pending anonymous events after signup
export async function syncPendingEvents(userId: string, restaurantId?: string): Promise<void> {
  try {
    const pendingEvents = JSON.parse(sessionStorage.getItem('pending_tracking_events') || '[]');
    
    if (pendingEvents.length === 0) return;

    console.log(`[TRACK] Syncing ${pendingEvents.length} pending events for user ${userId}`);

    for (const event of pendingEvents) {
      await supabase.from('user_activity_tracking').insert({
        user_id: userId,
        restaurant_id: restaurantId || null,
        event_type: event.event_type,
        event_data: event.event_data,
        created_at: event.created_at,
      });
    }

    sessionStorage.removeItem('pending_tracking_events');
    console.log('[TRACK] Pending events synced successfully');
  } catch (err) {
    console.error('Failed to sync pending events:', err);
  }
}

export async function updateRestaurantActivity(
  restaurantId: string,
  updates: {
    first_login_at?: string;
    onboarding_completed_at?: string;
    last_activity_at?: string;
    onboarding_step?: string;
  }
): Promise<void> {
  try {
    const { error } = await supabase
      .from('restaurants')
      .update(updates)
      .eq('id', restaurantId);

    if (error) {
      console.error('Error updating restaurant activity:', error);
    }
  } catch (err) {
    console.error('Failed to update restaurant activity:', err);
  }
}

export async function trackFirstLogin(restaurantId?: string): Promise<void> {
  await trackActivity('first_login', { timestamp: new Date().toISOString() }, restaurantId);
  
  if (restaurantId) {
    await updateRestaurantActivity(restaurantId, {
      first_login_at: new Date().toISOString(),
      last_activity_at: new Date().toISOString(),
    });
  }
}

export async function trackOnboardingStep(
  step: string,
  restaurantId?: string,
  additionalData: TrackingData = {}
): Promise<void> {
  const eventType = `onboarding_step_${step}` as ActivityEventType;
  await trackActivity(eventType, { step, ...additionalData }, restaurantId);
  
  if (restaurantId) {
    await updateRestaurantActivity(restaurantId, {
      onboarding_step: step,
      last_activity_at: new Date().toISOString(),
    });
  }
}
